import { type ModalDefinition } from "@/features/modals/types";
import { ProjectDetails } from "@/features/projects/cards/ProjectDetails";
import { type Opportunity, type UpdateOpportunity } from "@repo/api";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import { trackAnalytics } from "@repo/utils";
import { assertUnreachable } from "@repo/utils";
import { type PayloadAction } from "typesafe-actions";

import { CmpSelection } from "./CmpSelection";
import { type ReducerState, type ReducerStateActions } from "./reducer";

const Footer = ({
  update,
  disabled,
  state,
  actions,
}: {
  update: () => Promise<void | PayloadAction<
    "SET_ORIGINAL_OWNER",
    string | undefined
  >>;
  disabled?: boolean;
  state: ReducerState;
  actions: ReducerStateActions;
}) => (
  <Button
    text={t("action.save")}
    disabled={disabled}
    onClick={() => {
      update().then(() => actions.setStep("DETAILS"));
      trackAnalytics("save_action");
    }}
    type="primary"
    loading={state.updateLoading}
  />
);

export const getView = (
  state: ReducerState,
  actions: ReducerStateActions,
  opportunity: Opportunity,
  update: (newOpportunity: UpdateOpportunity) => Promise<void>,
  pushModal: (modal: ModalDefinition) => void
) => {
  switch (state.currentStep) {
    case "DETAILS":
      return {
        title: t("label.details"),
        view: (
          <ProjectDetails
            editable={!opportunity.lockStatus}
            opportunity={opportunity}
            openCmpModal={() => actions.setStep("CUSTOMER_MEETING_POINT")}
            openHfbModal={() =>
              pushModal({
                type: "SELECT_DEPARTMENT",
                selected: state.hfb ?? [],
                onAsyncUpdate: async (newHfb) => {
                  actions.setHfb(newHfb);
                  await update({
                    id: opportunity.id,
                    hfb: newHfb,
                  });
                },
              })
            }
            openTagModal={() =>
              pushModal({
                type: "SELECT_TAG",
                selected: state.tags ?? [],
                opportunityId: opportunity.id,
                // eslint-disable-next-line @typescript-eslint/require-await
                onAsyncUpdate: async (newTags) => {
                  actions.setTags(newTags);
                },
              })
            }
          />
        ),
        onBack: undefined,
        footer: undefined,
      };
    case "CUSTOMER_MEETING_POINT":
      return {
        title: t("label.cmp"),
        view: <CmpSelection state={state} actions={actions} />,
        onBack: () => {
          actions.setStep("DETAILS");
          actions.setCmp(opportunity.customerMeetingPointBuCode);
        },
        footer: (
          <Footer
            state={state}
            actions={actions}
            update={async () => {
              actions.setLoading(true);
              await update({
                id: opportunity.id,
                customerMeetingPointBuCode: state.cmp,
              });
              actions.setLoading(false);
            }}
          />
        ),
      };
  }
  assertUnreachable(state.currentStep);
};
